import { Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonSwitchInput } from 'components/common/form/switch';
import { SettingForm } from 'components/common/settings-dialog/form';
import { SettingRow } from 'components/common/settings-dialog/row';
import env from 'config';
import { CookieKey, DragDropEngine } from 'enums/cookies.enums';
import { t } from 'i18next';
import { TrainingMode } from 'lib_ts/enums/machine.enums';
import { RadixAppearance } from 'lib_ts/enums/radix-ui';
import { LANGUAGE_OPTIONS, LanguageCode } from 'lib_ts/enums/translation';
import { useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import { useCookiesStore } from 'stores/use-cookies-store';
import useMiscStore from 'stores/use-misc-store';

export const InterfaceSection = () => {
  const app = useCookiesStore((s) => s.app);

  const current = useAuthStore((s) => s.current);

  const [languageOptions] = useState(
    LANGUAGE_OPTIONS.filter(
      (o) =>
        // always include the user's language even if not enabled for this env
        current.language === o.value ||
        // include all languages enabled for this env
        env.languages.includes(o.value)
    )
  );

  const [isTouch] = useState(navigator.maxTouchPoints > 0);

  const isDarkMode = useMiscStore((s) => s.isDarkMode);

  return (
    <ErrorBoundary identifier="InterfaceSection">
      <SettingForm>
        {languageOptions.length > 1 && (
          <SettingRow
            separatorAfter
            header="common.interface-language"
            description={
              <Text>{t('common.interface-language-description')}</Text>
            }
            input={
              <CommonSelectInput
                id="interface-language"
                name="language"
                data-value={current.language}
                options={languageOptions}
                value={current.language}
                onChange={(v) => {
                  const code = v as LanguageCode;

                  // updating the current user will automatically cascade to i18n language upon success
                  useAuthStore.getState().updateUser({
                    _id: current.userID,
                    language: code,
                  });
                }}
              />
            }
          />
        )}

        <SettingRow
          header="settings.appearance"
          description={<Text>{t('settings.appearance-msg')}</Text>}
          input={
            <CommonSelectInput
              id="interface-appearance"
              name="appearance"
              data-value={
                isDarkMode ? RadixAppearance.Dark : RadixAppearance.Light
              }
              className="capitalize"
              options={[
                {
                  label: t('settings.dark-mode'),
                  value: RadixAppearance.Dark,
                },
                {
                  label: t('settings.light-mode'),
                  value: RadixAppearance.Light,
                },
                {
                  label: t('settings.system-mode'),
                  value: RadixAppearance.Inherit,
                },
              ]}
              value={isDarkMode ? RadixAppearance.Dark : RadixAppearance.Light}
              onChange={(v) =>
                useMiscStore.getState().setAppearance(v as RadixAppearance)
              }
            />
          }
        />

        {(Object.values(TrainingMode).length > 1 ||
          !Object.values(TrainingMode).includes(
            current.training_mode as TrainingMode
          )) && (
          // ! deprecate this if we won't have more than 1 training mode going forward
          // only show the setting if the current value is illegal or if there are more modes added
          <SettingRow
            separatorBefore
            header="common.training-mode"
            description={<Text>{t('settings.training-mode-msg')}</Text>}
            input={
              <CommonSelectInput
                id="interface-training-mode"
                name="training_mode"
                data-value={current.training_mode}
                className="capitalize"
                options={[
                  {
                    label: t('settings.auto-train'),
                    value: TrainingMode.Quick,
                  },
                ]}
                value={current.training_mode}
                onChange={async (v) => {
                  const mode = v as TrainingMode;

                  // save the setting to the database (to persist across sessions)
                  await useAuthStore.getState().setTrainingMode(mode);
                }}
              />
            }
          />
        )}

        <SettingRow
          separatorBefore
          header="settings.auto-assign-videos"
          description={<Text>{t('settings.auto-assign-videos-msg')}</Text>}
          input={
            <Flex justify="end">
              <CommonSwitchInput
                id="interface-auto-videos"
                data-testid="AutoVideosToggle"
                checked={!current.disable_auto_videos}
                onCheckedChange={(v) => {
                  useAuthStore.getState().updateUser({
                    disable_auto_videos: !v,
                  });
                }}
              />
            </Flex>
          }
        />

        {isTouch && (
          <SettingRow
            separatorBefore
            header="common.drag-drop-mode"
            description={<Text>{t('settings.drag-drop-mode-msg')}</Text>}
            input={
              <Flex justify="end">
                <CommonSwitchInput
                  id="interface-touch"
                  checked={app.drag_drop_engine === DragDropEngine.touch}
                  onCheckedChange={(v) => {
                    useCookiesStore.getState().setCookie(CookieKey.app, {
                      drag_drop_engine: v
                        ? DragDropEngine.touch
                        : DragDropEngine.click,
                    });

                    NotifyHelper.info({
                      message_md: t('settings.refresh-page-for-effect-msg'),
                      delay_ms: 0,
                      inbox: true,
                    });
                  }}
                />
              </Flex>
            }
          />
        )}

        <SettingRow
          separatorBefore
          header="common.placeholder-folders"
          description={<Text>{t('settings.placeholder-folders-msg')}</Text>}
          input={
            <Flex justify="end">
              <CommonSwitchInput
                id="interface-placeholder-folders"
                data-testid="PlaceholderFoldersToggle"
                checked={current.placeholder_folders}
                onCheckedChange={(v) => {
                  useAuthStore.getState().updateUser({
                    placeholder_folders: v,
                  });
                }}
              />
            </Flex>
          }
        />

        {env.enable.toggle_plate_ellipses && (
          <SettingRow
            separatorBefore
            header="common.plate-ellipses"
            description={<Text>{t('settings.plate-ellipses-msg')}</Text>}
            input={
              <Flex justify="end">
                <CommonSwitchInput
                  id="interface-show-ellipses"
                  checked={current.plate_show_ellipses}
                  onCheckedChange={(v) => {
                    useAuthStore.getState().updateUser({
                      plate_show_ellipses: v,
                    });
                  }}
                />
              </Flex>
            }
          />
        )}

        {env.enable.toggle_plate_debug && (
          <SettingRow
            separatorBefore
            header="common.plate-debug"
            description={<Text>{t('settings.plate-debug-msg')}</Text>}
            input={
              <Flex justify="end">
                <CommonSwitchInput
                  id="interface-show-plate-debug"
                  checked={app.plate_show_debug}
                  onCheckedChange={(v) =>
                    useCookiesStore.getState().setCookie(CookieKey.app, {
                      plate_show_debug: v,
                    })
                  }
                />
              </Flex>
            }
          />
        )}

        {env.enable.toggle_plate_safety_controls && (
          <>
            <SettingRow
              separatorBefore
              header="common.safety-limits"
              description={<Text>{t('settings.safety-limits-msg')}</Text>}
              input={
                <Flex justify="end">
                  <CommonSwitchInput
                    id="interface-ellipse-limits"
                    checked={app.plate_limit_by_ellipse}
                    onCheckedChange={(v) =>
                      useCookiesStore.getState().setCookie(CookieKey.app, {
                        plate_limit_by_ellipse: v,
                      })
                    }
                  />
                </Flex>
              }
            />
            <SettingRow
              separatorBefore
              header="common.safety-warnings"
              description={<Text>{t('settings.safety-warnings-msg')}</Text>}
              input={
                <Flex justify="end">
                  <CommonSwitchInput
                    id="interface-ellipse-warnings"
                    checked={app.plate_show_ellipse_warnings}
                    onCheckedChange={(v) =>
                      useCookiesStore.getState().setCookie(CookieKey.app, {
                        plate_show_ellipse_warnings: v,
                      })
                    }
                  />
                </Flex>
              }
            />
          </>
        )}

        <SettingRow
          separatorBefore
          header="common.debug-messages"
          description={<Text>{t('settings.debug-messages-msg')}</Text>}
          input={
            <Flex justify="end">
              <CommonSwitchInput
                id="interface-debug-toasts"
                checked={app.enable_debug_toasts}
                onCheckedChange={(v) => {
                  useCookiesStore.getState().setCookie(CookieKey.app, {
                    enable_debug_toasts: v,
                  });
                }}
              />
            </Flex>
          }
        />
      </SettingForm>
    </ErrorBoundary>
  );
};
